/**
 * El flujo en vivo, en el cliente.  (T048, FR-012, FR-013)
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  EL NAVEGADOR NO SONDEA. Se conecta UNA vez y recibe empujado.             ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * `EventSource` reconecta solo si se cae, y manda su `Last-Event-ID`: el backend
 * le reproduce lo perdido, o le dice `stream.resync` si no puede (y entonces
 * recargamos la lista entera — UNA vez, no en bucle).
 *
 *   · conversation.upsert → `setQueryData`: mezcla la fila, sin ir a la red.
 *     «Al equipo» y el hilo abierto se invalidan juntos (033). La portada
 *     y el resumen NO: preguntarían a todas las agendas.
 *   · poller.heartbeat    → actualiza la señal de conexión (liveness por dato).
 *   · stream.resync       → invalida la lista de conversaciones (el anillo no
 *     pudo reproducir). El calendario NO: FR-030, manda el intervalo elegido.
 *   · session.expired     → al login (la sesión murió mientras mirábamos).
 *
 * ⚠️ NADA de `refetchInterval`: lo prohíbe una regla de ESLint. La frescura
 * del calendario abierto la elige quien mira (15 s / 30 s / 1 min / Manual).
 */
import type { QueryClient } from '@tanstack/react-query'

import type { SnapshotConexion } from './conexion'
import { ordenarPorGravedad } from './orden'
import type { DatosLatido, FilaConversacion, RespuestaLista } from './tipos'

/**
 * PREFIJO de la caché de la lista viva. Sirve para invalidar o limpiar **todos**
 * los negocios de golpe (TanStack casa por prefijo).
 */
export const CLAVE_CONVERSACIONES = ['conversations'] as const

/**
 * La clave concreta: **una por negocio** (FR-037, la consola cuelga del negocio).
 *
 * ⚠️ Antes era una sola clave global y el flujo SSE escribía ahí. Con la consola
 * acotada a un negocio eso ya no vale: el flujo de un `admin` trae TODOS los
 * negocios, así que una clave común metería conversaciones del negocio B en la
 * consola del A — una fuga de aislamiento en la propia pantalla (Principio VII).
 */
export const claveConversaciones = (accountId: number) => ['conversations', accountId] as const

/**
 * Lo que un upsert de ESE negocio puede volver a pedir.  (033 · FR-001/002)
 *
 * La consola se parchea en caché. «Al equipo» y el hilo abierto, sí:
 * son baratos y solo recargan si esa pantalla está montada. La portada,
 * el resumen y el pulso NO: `/overview` pregunta a las ocho agendas.
 */
export function clavesAInvalidarTrasUpsert(
    accountId: number,
    conversationId: number,
): ReadonlyArray<readonly unknown[]> {
    return [
        ['business-conversation', accountId, conversationId],
        ['elevadas', String(accountId)],
    ]
}

export const COALESCE_INVALIDACION_MS = 2_000

/** Junta invalidaciones de la misma clave. Varios upserts → una ida a la red. */
export function crearCoalescer(
    aplicar: (clave: readonly unknown[]) => void,
    esperaMs = COALESCE_INVALIDACION_MS,
): { pedir: (clave: readonly unknown[]) => void; cancelar: () => void } {
    const pendientes = new Map<string, readonly unknown[]>()
    let timer: ReturnType<typeof setTimeout> | null = null

    const vaciar = (): void => {
        timer = null
        const lote = [...pendientes.values()]
        pendientes.clear()
        for (const clave of lote) aplicar(clave)
    }

    return {
        pedir(clave) {
            pendientes.set(JSON.stringify(clave), clave)
            if (!timer) timer = setTimeout(vaciar, esperaMs)
        },
        cancelar() {
            if (timer) clearTimeout(timer)
            timer = null
            pendientes.clear()
        },
    }
}

/** ¿Este desenlace implica que la agenda cambió? */
export function outcomeTocaAgenda(outcome: string): boolean {
    return outcome === 'booked' || outcome === 'modified' || outcome === 'cancelled'
}

interface Opciones {
    queryClient: QueryClient
    onConexion: (s: SnapshotConexion) => void
    onSesionExpirada: () => void
}

const SNAP_INICIAL: SnapshotConexion = {
    reconectando: false,
    ultimoLatidoRecibidoEn: null,
    lastOkAt: null,
    serverNow: null,
}

const LISTA_VACIA: RespuestaLista = { total: 0, realCount: 0, testCount: 0, items: [] }

/**
 * Los campos de IDENTIDAD son STICKY: una vez resueltos (por el worker, vía SSE,
 * o en la carga inicial) no se pierden. Una observación posterior llega con
 * accountId/nombre/enlace en null (el bucle no los rellena); sin esto, borraría
 * el negocio ya resuelto en pantalla. `synthetic` también pega: una vez test,
 * sigue test.
 */
function fusionarCampos(prev: FilaConversacion, nueva: FilaConversacion): FilaConversacion {
    return {
        ...nueva,
        accountId: nueva.accountId ?? prev.accountId,
        businessName: nueva.businessName ?? prev.businessName,
        chatwootUrl: nueva.chatwootUrl ?? prev.chatwootUrl,
        synthetic: nueva.synthetic || prev.synthetic,
    }
}

/** Mezcla una fila empujada en la lista cacheada, re-ordenando por gravedad. */
export function fusionarFila(prev: RespuestaLista | undefined, fila: FilaConversacion): RespuestaLista {
    const base = prev ?? LISTA_VACIA
    const anterior = base.items.find((i) => i.conversationId === fila.conversationId)
    const combinada = anterior ? fusionarCampos(anterior, fila) : fila
    const items = anterior
        ? base.items.map((i) => (i.conversationId === fila.conversationId ? combinada : i))
        : [combinada, ...base.items]
    return { ...base, items: ordenarPorGravedad(items) }
}

export class ClienteVivo {
    private es: EventSource | null = null
    private snap: SnapshotConexion = { ...SNAP_INICIAL }
    private readonly coalescer: ReturnType<typeof crearCoalescer>

    constructor(private readonly op: Opciones) {
        this.coalescer = crearCoalescer((queryKey) => {
            void this.op.queryClient.invalidateQueries({ queryKey })
        })
    }

    conectar(): void {
        if (this.es) return
        const es = new EventSource('/stream/live', { withCredentials: true })
        this.es = es

        es.onopen = () => this.parchear({ reconectando: false })
        // EventSource reintenta solo; `onerror` marca que estamos sin conexión.
        es.onerror = () => this.parchear({ reconectando: true })

        es.addEventListener('conversation.upsert', (ev) => this.alUpsert(ev as MessageEvent))
        es.addEventListener('poller.heartbeat', (ev) => this.alLatido(ev as MessageEvent))
        es.addEventListener('stream.resync', () => this.alResync())
        es.addEventListener('session.expired', () => this.alSesionExpirada())
    }

    desconectar(): void {
        this.coalescer.cancelar()
        this.es?.close()
        this.es = null
    }

    private parchear(parcial: Partial<SnapshotConexion>): void {
        this.snap = { ...this.snap, ...parcial }
        this.op.onConexion(this.snap)
    }

    private alUpsert(ev: MessageEvent): void {
        const fila = JSON.parse(ev.data as string) as FilaConversacion
        // Cada fila va al cubo de SU negocio: es lo que mantiene el vivo sin
        // mezclar negocios en pantalla.
        //
        // Sin `accountId` no hay cubo al que ir, y no se pierde nada real: hoy
        // las únicas filas sin negocio son del LABORATORIO (medido el 2026-07-29:
        // 114 sin cuenta en 7 días, las 114 sintéticas; cero reales). Las reales
        // llegan siempre con su cuenta desde el ADR 0003. Y las que no tienen
        // negocio siguen contándose en la portada, que es su sitio: no pertenecen
        // a la consola de ningún negocio.
        if (fila.accountId === null) return
        this.op.queryClient.setQueryData<RespuestaLista>(claveConversaciones(fila.accountId), (prev) =>
            fusionarFila(prev, fila),
        )
        // 033: no se invalida la portada ni el resumen. «Al equipo» y el
        // hilo abierto sí, juntos (FR-002).
        for (const queryKey of clavesAInvalidarTrasUpsert(fila.accountId, fila.conversationId)) {
            this.coalescer.pedir(queryKey)
        }
        // Spec 015 · FR-030: el intervalo del calendario abierto manda.
        // Un booked del bot no lo atraviesa —con un lote del laboratorio
        // eso era recargar EasyAppointments cada pocos segundos.
    }

    private alLatido(ev: MessageEvent): void {
        const { lastOkAt, serverNow } = JSON.parse(ev.data as string) as DatosLatido
        this.parchear({ reconectando: false, ultimoLatidoRecibidoEn: Date.now(), lastOkAt, serverNow })
    }

    private alResync(): void {
        // El anillo no pudo reproducir (la API reinició, o pasó mucho tiempo).
        // Recarga la lista de conversaciones, UNA vez. El calendario no:
        // FR-030, el intervalo elegido es el único refresco automático.
        void this.op.queryClient.invalidateQueries({ queryKey: CLAVE_CONVERSACIONES })
        void this.op.queryClient.invalidateQueries({ queryKey: ['business'] })
        void this.op.queryClient.invalidateQueries({ queryKey: ['elevadas'] })
        void this.op.queryClient.invalidateQueries({ queryKey: ['pulso-trends'] })
        void this.op.queryClient.invalidateQueries({ queryKey: ['admin-overview'] })
    }

    private alSesionExpirada(): void {
        this.desconectar()
        this.op.onSesionExpirada()
    }
}
